import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBatch } from '@/contexts/BatchContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Users, GraduationCap, FileText, Clock, PlusCircle, Upload, ArrowRight } from 'lucide-react';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { dashboardApi } from '@/lib/api';
import RecentActivities from '@/components/dashboard/RecentActivities';

const DashboardPage = () => {
  // We need the auth context but don't use user directly
  useAuth();
  const { currentBatch } = useBatch();

  // State for dashboard data
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalClasses: 0,
    totalTests: 0,
    upcomingTests: 0
  });
  // Removed recentActivities - now using RecentActivities component
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [statsData] = await Promise.all([
          dashboardApi.getStats()
          // dashboardApi.getRecentActivities() // Now using RecentActivities component
        ]);

        setStats(statsData);
        // setRecentActivities(activitiesData);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');

        // Fallback to mock data
        setStats({
          totalStudents: 0,
          totalClasses: 0,
          totalTests: 0,
          upcomingTests: 0
        });
        // setRecentActivities([
        //   { id: 1, time: 'Today at 10:30 AM', description: 'New student John Doe was added to Computer Science class' },
        //   { id: 2, time: 'Yesterday at 2:15 PM', description: 'Physics Midterm test was published' },
        //   { id: 3, time: 'Yesterday at 11:45 AM', description: 'New class "Advanced Mathematics" was created' },
        //   { id: 4, time: '2 days ago', description: '5 students completed the Math Quiz' }
        // ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentBatch]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <div className="text-sm text-gray-500">
            Current Batch: <span className="font-medium text-primary">{currentBatch?.year || '2023-2024'}</span>
          </div>
        </div>
        <LoadingSpinner message="Loading dashboard data..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="text-sm text-gray-500">
          Current Batch: <span className="font-medium text-primary">{currentBatch?.year || '2023-2024'}</span>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800 text-sm">
            {error}. Showing fallback data.
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-sm font-medium">Total Students</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalStudents}</div>
            <div className="flex justify-between items-center mt-4">
              <Link to="/students" className="text-primary text-sm font-medium flex items-center hover:underline">
                View all students <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <GraduationCap className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-sm font-medium">Total Classes</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalClasses}</div>
            <div className="flex justify-between items-center mt-4">
              <Link to="/classes" className="text-primary text-sm font-medium flex items-center hover:underline">
                View all classes <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-sm font-medium">Total Tests</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalTests}</div>
            <div className="flex justify-between items-center mt-4">
              <Link to="/tests" className="text-primary text-sm font-medium flex items-center hover:underline">
                View all tests <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 pt-6">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-sm font-medium">Upcoming Tests</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.upcomingTests}</div>
            <div className="flex justify-between items-center mt-4">
              <Link to="/tests" className="text-primary text-sm font-medium flex items-center hover:underline">
                View upcoming tests <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Recent Activity */}
        <RecentActivities limit={6} className="border-0 shadow-sm" />

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="justify-start h-auto py-3 border-gray-200" asChild>
                <Link to="/students/new" className="flex flex-col items-start">
                  <div className="flex items-center w-full">
                    <PlusCircle className="mr-2 h-4 w-4 text-primary" />
                    <span>Add Student</span>
                  </div>
                </Link>
              </Button>
              <Button variant="outline" className="justify-start h-auto py-3 border-gray-200" asChild>
                <Link to="/classes/new" className="flex flex-col items-start">
                  <div className="flex items-center w-full">
                    <PlusCircle className="mr-2 h-4 w-4 text-primary" />
                    <span>Create Class</span>
                  </div>
                </Link>
              </Button>
              <Button variant="outline" className="justify-start h-auto py-3 border-gray-200" asChild>
                <Link to="/tests/new" className="flex flex-col items-start">
                  <div className="flex items-center w-full">
                    <PlusCircle className="mr-2 h-4 w-4 text-primary" />
                    <span>Create Test</span>
                  </div>
                </Link>
              </Button>
              <Button variant="outline" className="justify-start h-auto py-3 border-gray-200" asChild>
                <Link to="/students/import" className="flex flex-col items-start">
                  <div className="flex items-center w-full">
                    <Upload className="mr-2 h-4 w-4 text-primary" />
                    <span>Import Students</span>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
